import React, {
  memo,
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Collapse } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export function getChildrens(itm) {
  function flatInnter(itms) {
    return itms.reduce((R, child) => {
      if (child.children) {
        return [...R, child.id, ...flatInnter(child.children)];
      }
      return [...R, child.id];
    }, []);
  }
  if (itm.children) {
    return flatInnter(itm.children);
  }
  return [];
}

function TreeviewListItem({
  item,
  openedItems,
  setOpenedItems,
  selectedItems,
  setSelectedItems,
  selection,
  currentItem,
  setCurrentItem,
  separated,
}) {
  const [open, setOpen] = useState(openedItems.includes(item.id));
  const [children, setChildren] = useState([]);
  const [firstChildren, setFirstChildren] = useState([]);
  const [childrenOpen, setChildrenOpen] = useState(false);
  const checkRef = useRef();

  const handleOnExiting = useCallback(
    () => setOpenedItems(openedItems.filter((openedItem) => openedItem !== item.id)),
    [item.id, openedItems, setOpenedItems],
  );
  const handleEntering = useCallback(
    () => setOpenedItems([...openedItems, item.id]),
    [item.id, openedItems, setOpenedItems],
  );
  const handleCheckboxChange = useCallback(
    (e) => {
      if (e.target.checked) {
        if (!separated && item.children && item.children.length > 0) {
          setSelectedItems([...selectedItems, item.id, ...children]);
        } else {
          setSelectedItems([...selectedItems, item.id]);
        }
      } else if (!separated && item.children && item.children.length > 0) {
        setSelectedItems(
          selectedItems.filter(
            (selectedItem) => selectedItem !== item.id && !children.includes(selectedItem),
          ),
        );
      } else {
        setSelectedItems(
          selectedItems.filter((selectedItem) => selectedItem !== item.id),
        );
      }
    },
    [children, item.children, item.id, selectedItems, separated, setSelectedItems],
  );

  useEffect(() => {
    setChildren(getChildrens(item));
    if (item.children) {
      setFirstChildren(item.children.map((child) => child.id));
    }
  }, [item]);

  useEffect(() => {
    setChildrenOpen(openedItems.some((itm) => firstChildren.indexOf(itm) !== -1));
  }, [children, firstChildren, openedItems]);

  useEffect(() => {
    if (checkRef.current) {
      const childrenSelected = selectedItems.some(
        (selectedItem) => children.indexOf(selectedItem) !== -1,
      );
      const allChildrenSelected = children.length > 0 && children.every(
        (child) => selectedItems.includes(child),
      );
      if (separated) {
        checkRef.current.checked = selectedItems.includes(item.id);
        checkRef.current.indeterminate = false;
      } else {
        if (childrenSelected && checkRef.current) {
          checkRef.current.indeterminate = true;
        }
        if (!childrenSelected && checkRef.current) {
          checkRef.current.indeterminate = false;
        }
        if (allChildrenSelected && checkRef.current) {
          checkRef.current.indeterminate = false;
          checkRef.current.checked = true;
        }
        if (!allChildrenSelected && checkRef.current) {
          checkRef.current.checked = false;
        }
      }
    }
  }, [selectedItems, children, separated, item.id]);

  return (
    <li className="treeview-list-item">
      {item.children ? (
        <>
          <div className={classNames('toggle-container', { selected: currentItem === item.id })}>
            {selection && setSelectedItems && (
              <input
                type="checkbox"
                className="form-check-input"
                onChange={handleCheckboxChange}
                ref={checkRef}
                disabled={item.disabled}
              />
            )}
            <Button
              variant="link"
              className={classNames('collapse-toggle', {
                collapsed: open || item.expanded,
              })}
              onClick={() => {
                setOpen(!open);
                if (setCurrentItem) setCurrentItem(item.id);
              }}
            >
              <p
                className={classNames('treeview-text', { selection })}
              >
                {item.icon && (
                // eslint-disable-next-line react/no-danger
                <span
                  className={classNames('treeview-icon', item.iconClass)}
                  dangerouslySetInnerHTML={{ __html: item.icon }}
                />
                )}
                {item.faIcon && (
                <FontAwesomeIcon className={classNames('treeview-icon me-1 ', item.iconClass)} icon={item.faIcon} />
                )}
                {item.name}
              </p>
            </Button>
          </div>
          <Collapse
            in={open}
            onExiting={handleOnExiting}
            onEntering={handleEntering}
          >
            <ul
              className={classNames('treeview-list ps-3', {
                'collapse-hidden': !open,
                'collapse-show treeview-border': open,
                'treeview-border-transparent': childrenOpen,
              })}
            >
              {item.children.map((nestedItem, index) => (
                <TreeviewListItem
                  key={nestedItem.id}
                  item={nestedItem}
                  index={index}
                  openedItems={openedItems}
                  setOpenedItems={setOpenedItems}
                  selectedItems={selectedItems}
                  setSelectedItems={setSelectedItems}
                  selection={selection}
                  currentItem={currentItem}
                  setCurrentItem={setCurrentItem}
                  separated={separated}
                />
              ))}
            </ul>
          </Collapse>
        </>
      ) : (
        <div className={classNames('treeview-item', { selected: currentItem === item.id })}>
          {selection && (
            <input
              type="checkbox"
              className="form-check-input"
              onChange={handleCheckboxChange}
              checked={selectedItems.includes(item.id)}
              disabled={item.disabled}
            />
          )}
          <Button
            variant="link"
            className="flex-1"
            onClick={() => {
              if (setCurrentItem) setCurrentItem(item.id);
            }}
          >
            <p className="treeview-text">
              {item.icon && (
              // eslint-disable-next-line react/no-danger
              <span
                className={classNames('treeview-icon', item.iconClass)}
                dangerouslySetInnerHTML={{ __html: item.icon }}
              />
              )}
              {item.faIcon && (
                <FontAwesomeIcon
                  icon={item.faIcon}
                  className={classNames('treeview-icon me-1', item.iconClass)}
                  size="xs"
                />
              )}
              {item.name}
            </p>
          </Button>
        </div>
      )}
    </li>
  );
}

TreeviewListItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    expanded: PropTypes.bool,
    name: PropTypes.node.isRequired,
    icon: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
    iconClass: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.shape({})),
    faIcon: PropTypes.shape({}),
    disabled: PropTypes.bool,
  }).isRequired,
  openedItems: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  setOpenedItems: PropTypes.func.isRequired,
  selectedItems: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  setSelectedItems: PropTypes.func,
  selection: PropTypes.bool,
  currentItem: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setCurrentItem: PropTypes.func,
  separated: PropTypes.bool,
};

TreeviewListItem.defaultProps = {
  openedItems: [],
  selectedItems: [],
  selection: false,
  setSelectedItems: null,
  currentItem: null,
  setCurrentItem: null,
  separated: false,
};

export default memo(TreeviewListItem);
